import type { ComponentProps } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { clearSavedUserFilter } from '@/clientOnlyUtils/clientUtils';
import { FirebaseError } from 'firebase/app';
import {
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { motion } from 'framer-motion';
import React, { useActionState } from 'react';
import { toast } from 'react-hot-toast';

import { isNotEmpty } from '@poseidon/components/common';
import { Button, Input, Label, PasswordInput } from '@poseidon/components/Input2';
import { Scrollable } from '@poseidon/components/Scrollable';
import { Communication, General, Security } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import type { NextPageWithLayout } from './_app';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { GoogleIcon } from '~/components/CompanyIcons';
import { useExtensionContext } from '~/components/extension.context';
import { useOnboardingContext } from '~/components/Onboarding/onboarding.context';
import { LogoIcon } from '~/components/ToneIcons';

type FormState = {
  errors?: {
    root?: string;
    email?: string;
    password?: string;
  };
  email?: string;
  password?: string;
};

const LoginPage: NextPageWithLayout = () => {
  const router = useRouter();
  const [, setQuery] = useRouterQuery();
  const [, { postMessage }] = useExtensionContext();
  const utils = api.useUtils();
  const generateTokenMutation = api.auth.token2.useMutation({
    onSuccess: () => {
      void utils.user.me.invalidate();
    },
  });
  const isResetPasswordSuccess = router.query.state === 'resetPasswordSuccess';
  // /signup's "account exists" panel links here with the address prefilled (POS-2510)
  const emailFromQuery = typeof router.query.email === 'string' ? router.query.email : undefined;
  const [loadingWithGoogle, setLoadingWithGoogle] = React.useState(false);
  const [, { resetOnboarding }] = useOnboardingContext();

  const [state, submitAction, isPending] = useActionState<FormState | undefined, FormData>(async (_, formData) => {
    const emailValue = formData.get('email');
    const passwordValue = formData.get('password');

    const email = emailValue instanceof Object ? undefined : emailValue?.toString();
    const password = passwordValue instanceof Object ? undefined : passwordValue?.toString();

    if (!isNotEmpty(email)) {
      return { errors: { email: 'We need your email to get started' }, email };
    }
    if (!isNotEmpty(password)) {
      return { errors: { password: 'Invalid password' }, email };
    }

    try {
      if (email != null && password != null) {
        await signInWithEmailAndPassword(getAuth(), email, password);
        const token = await generateTokenMutation.mutateAsync({
          userAgent: window.navigator.userAgent,
        });
        clearSavedUserFilter();
        postMessage({ type: 'LOGIN', data: { token } });
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/wrong-password') {
          return { errors: { password: 'Invalid password' }, email, password };
        } else if (error.code === 'auth/invalid-email') {
          return { errors: { email: 'Your email is invalid' }, email, password };
        } else if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
          // Firebase SDK ≥ 10 reports a wrong password as invalid-credential (enumeration protection),
          // so this is the branch a mistyped password lands in — say so instead of a generic "Oops".
          return { errors: { root: 'Wrong email or password. Please check both and try again.' }, email, password };
        } else if (error.code === 'auth/too-many-requests') {
          return { errors: { root: 'Too many failed login attempts, please wait a few minutes' }, email, password };
        } else {
          return { errors: { root: 'Oops! We could not log you in. Please try again' }, email, password };
        }
      }
    }
  }, undefined);

  const loginWithGoogle = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      setLoadingWithGoogle(true);
      const userResp = await signInWithPopup(auth, provider);
      clearSavedUserFilter();
      const userAdditionalInfo = getAdditionalUserInfo(userResp);

      if (userAdditionalInfo?.isNewUser) {
        // Clear any previous onboarding state for new users
        localStorage.removeItem('onboardingCompleted');
        localStorage.removeItem('selectedTier');
        localStorage.removeItem('icpFormData');

        setQuery((q) => ({ ...q, redirect: '/onboarding' }));
        resetOnboarding();
      }
    } catch (e) {
      // COOP (Cross-Origin-Opener-Policy) can block the popup from communicating
      // back, causing signInWithPopup to error even though auth succeeded.
      // Check if the user is actually signed in despite the error.
      if (auth.currentUser) {
        clearSavedUserFilter();
        router.reload();
        return;
      }

      if (e instanceof FirebaseError) {
        if (e.code === 'auth/popup-closed-by-user') {
          toast.error('Looks like you closed the popup. Please try again.');
        } else if (e.code === 'auth/too-many-requests') {
          toast.error('Too many failed login attempts, please wait a few minutes');
        } else if (e.code === 'auth/unauthorized-domain') {
          toast.error(
            <span>
              Looks like the domain you are using is not authorized. Please visit{' '}
              <b>
                <a href="https://useposeidon.com/login">https://useposeidon.com/login</a>
              </b>{' '}
              to login.
            </span>,
          );
        } else {
          toast.error('Oops! We could not log you in. Please try again');
        }
      } else {
        toast.error('Oops! We could not log you in. Please try again');
      }
    } finally {
      setLoadingWithGoogle(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <LogoIcon className="size-12 text-white" />
        <h2 className="mt-8 font-(family-name:--display-font) text-3xl font-bold tracking-tight text-white">Log in</h2>
        <p className="my-4 mb-8 text-base text-white/60">
          {isResetPasswordSuccess
            ? 'Your password has been reset. Please log in with your new password.'
            : 'Dive right back into your prospecting missions.'}
        </p>
        <form className="flex w-full flex-col items-stretch gap-4" action={submitAction}>
          <div className="flex flex-col gap-1">
            <Label className="text-white/80">Email</Label>
            <Input
              name="email"
              icon={<Communication.Mail01 />}
              placeholder="Enter your email"
              defaultValue={state?.email ?? emailFromQuery}
              error={state?.errors?.email}
              size="lg"
              className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:border-white/40! focus-within:bg-white/15! focus-within:outline-hidden! hover:bg-white/15!"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-white/80">Password</Label>
            <PasswordInput
              name="password"
              icon={<Security.Key01 />}
              placeholder="Enter a password"
              error={state?.errors?.password}
              size="lg"
              className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:border-white/40! focus-within:bg-white/15! hover:bg-white/15! [&>.w-\[1px\]]:bg-white/20!"
            />
          </div>

          {state?.errors?.root != null && <div className="text-fair-pink-600">{state?.errors?.root}</div>}
          <Button
            size="lg"
            type="submit"
            color="blue"
            textAlign="center"
            disabled={isPending}
            icon={isPending ? <WaveSpinner /> : undefined}>
            Log in
          </Button>
          <div className="text-center text-sm font-semibold text-white/50">or</div>
          <Button
            size="lg"
            type="button"
            textAlign="center"
            disabled={loadingWithGoogle || isPending}
            icon={loadingWithGoogle ? <WaveSpinner /> : <GoogleIcon />}
            className="border-white/20 bg-white/10 text-white hover:bg-white/15"
            onClick={loginWithGoogle}>
            Login with google
          </Button>
          <div className="text-center text-sm text-white/60 [&_a:hover]:text-white [&_a]:text-white/60">
            Don&rsquo;t have an account yet?&nbsp;
            <Link href="/signup">Sign up</Link>
          </div>
          <div className="text-center text-sm text-white/60 [&_a:hover]:text-white [&_a]:text-white/60">
            <Link href="/forget">I need help with my password</Link>
          </div>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

LoginPage.layout = 'onlyIfNotLoggedIn';
export default LoginPage;

export function AuthLayout({ children }: ComponentProps<'div'>) {
  // Background set inline, not via a Tailwind arbitrary bg-url class: the compiled CSS is served from the
  // static-* CDN origin (assetPrefix), so a root-relative url() in CSS resolves against
  // the CDN bucket (404). Inline styles resolve against the page origin, which serves /public.
  return (
    <div
      className="relative flex h-screen w-screen overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: "url('/onboarding.webp')" }}>
      <div className="absolute inset-0 bg-black/40" />
      {/* Left side — hero text */}
      <div className="relative z-10 hidden flex-1 flex-col justify-center px-12 pb-24 lg:flex xl:px-20">
        <h1 className="font-(family-name:--display-font) text-5xl font-bold tracking-tight text-white lg:text-7xl">
          The Growth Platform for Wealth Advisors
        </h1>
        <ul className="mt-5 flex flex-col gap-2.5">
          {['More visibility', 'More conversations', 'More AUM'].map((item) => (
            <li
              key={item}
              className="flex items-center gap-2.5 text-xl font-normal tracking-tight text-amber-300/90 lg:text-2xl">
              <General.CheckCircle className="size-6 shrink-0 text-amber-300/90" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      {/* Right side — auth card */}
      <div className="relative z-10 flex h-full w-full shrink-0 items-center justify-center lg:w-auto">{children}</div>
    </div>
  );
}

export function AuthCard({ children }: ComponentProps<'div'>) {
  return (
    <div className="auth-glass">
      <Scrollable
        className="h-screen lg:w-[480px]"
        innerClassName="flex min-h-full flex-col items-center justify-center border-l border-white/15 bg-white/8 px-10 py-8 shadow-2xl backdrop-blur-xl sm:px-14">
        <motion.div
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: 'spring' }}
          className="flex w-full flex-col items-center">
          {children}
        </motion.div>
      </Scrollable>
    </div>
  );
}
